// OWNER: Agent C. Holding detail: valuation + its transaction history + a
// shortcut into the quick-entry form, pre-selecting this holding.
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { Text } from "@cloudflare/kumo/components/text";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { formatUnits, formatMoney, formatRelativeAge } from "@shared/format";
import type { Currency, HoldingValuation } from "@shared/types";
import { usePortfolioSummary, useTransactions, useIsAdmin } from "../../lib/hooks";
import { PageHeader } from "../../components/PageHeader";
import { GainBadge } from "../../components/GainBadge";
import { MoneyDual } from "../../components/MoneyDual";
import { TableSkeleton, CardSkeleton } from "../../components/Skeletons";

export default function HoldingDetailPage() {
  const { holdingId } = useParams();
  const id = Number(holdingId);
  const navigate = useNavigate();
  const summary = usePortfolioSummary();
  const transactions = useTransactions({ holdingId: id, limit: 25 });
  const isAdmin = useIsAdmin();

  if (summary.status === "loading") return <CardSkeleton />;
  if (summary.status === "error") return <Text variant="error">{summary.error.message}</Text>;

  let found: { holding: HoldingValuation; currency: Currency; fxRate: number } | null = null;
  for (const c of summary.data.countries) {
    for (const a of c.accounts) {
      const h = a.holdings.find((x) => x.id === id);
      if (h) {
        found = { holding: h, currency: a.currency, fxRate: a.currency === "GBP" ? summary.data.fx.GBPINR : a.currency === "USD" ? summary.data.fx.USDINR : 1 };
      }
    }
  }

  if (!found) {
    return <Empty title="Holding not found" description="It may have been removed or deactivated." />;
  }

  const { holding: h, currency, fxRate } = found;

  return (
    <div className="space-y-8 pb-8">
      <PageHeader
        title={h.name}
        description={h.symbol ?? undefined}
        action={
          <Button
            variant="primary"
            disabled={!isAdmin}
            title={isAdmin ? undefined : "Admins only"}
            onClick={() => navigate("/transactions/new", { state: { holdingId: id } })}
          >
            Add transaction
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Units" value={h.price != null ? formatUnits(h.units) : "—"} />
        <Stat label="Price" value={h.price != null ? formatMoney(h.price, currency) : "—"} />
        <Stat label="Invested" value={formatMoney(h.investedNative, currency)} />
        <Stat label="Gain" value={<GainBadge pct={h.gainPct} />} />
      </div>

      <div>
        <Text variant="secondary" size="sm">
          Current value
        </Text>
        <div className="mt-1">
          <MoneyDual valueNative={h.valueNative} nativeCurrency={currency} valueInr={h.valueInr} fxRate={currency !== "INR" ? fxRate : undefined} fxAsOf={summary.data.fx.asOf} size="lg" />
        </div>
        {h.priceAsOf && (
          <div className="mt-1">
            <Text variant="secondary" size="xs">
              Price as of {formatRelativeAge(h.priceAsOf)} ({h.priceSource ?? "unknown source"})
              {h.isStale ? " · stale" : ""}
            </Text>
          </div>
        )}
      </div>

      <div>
        <Text variant="heading" as="h2">
          Transaction history
        </Text>
        <div className="mt-3">
          {transactions.status === "loading" && <TableSkeleton rows={4} />}
          {transactions.status === "error" && <Text variant="error">Couldn't load transactions.</Text>}
          {transactions.status === "success" && transactions.data.items.length === 0 && (
            <Empty size="sm" title="No transactions yet" description="Transactions for this holding will show up here." />
          )}
          {transactions.status === "success" && transactions.data.items.length > 0 && (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Date</Table.Head>
                  <Table.Head>Type</Table.Head>
                  <Table.Head>Amount</Table.Head>
                  <Table.Head>Units</Table.Head>
                  <Table.Head>Note</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {transactions.data.items.map((t) => (
                  <Table.Row key={t.id}>
                    <Table.Cell>{t.txnDate}</Table.Cell>
                    <Table.Cell>{t.type}</Table.Cell>
                    <Table.Cell>{formatMoney(t.amount, t.currency)}</Table.Cell>
                    <Table.Cell>{t.units != null ? formatUnits(t.units) : "—"}</Table.Cell>
                    <Table.Cell>{t.note ?? "—"}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-kumo-hairline p-3">
      <Text variant="secondary" size="xs">
        {label}
      </Text>
      <div className="mt-1 text-base font-medium text-kumo-strong">{value}</div>
    </div>
  );
}
